import { inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { CurrentUserResponse } from '../../interfaces/usersmanagement-interface';
import { extractVisualKeys, isPlatformToken, SUPER_ADMIN_ROLE } from '../auth/jwt';

/**
 * Where to send a user who is signed in but not allowed here.
 *
 * Once this had to branch, because a SuperAdmin signed in here had no company features and
 * bouncing them to /dashboard span forever. Operators now sign in to the console instead and never
 * hold a session in this application, so there is one landing page again.
 */
export function homeRouteFor(_authService: AuthService): string {
  return '/dashboard';
}

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    // On the server, allow navigation (do not block or redirect)
    return true;
  }

  const token = authService.getToken();

  if (!token || !authService.isLoggedIn()) {
    // '/login' — the route that exists. This used to navigate to '/signin', which is not
    // declared anywhere, so an expired session landed on the wildcard instead of the login form.
    router.navigate(['/login']);
    return false;
  }

  // A platform token has no business in this application at all. It cannot reach a company
  // endpoint since the API began signing the two families with different keys, so leaving one in
  // place would mean a session where every request 401s. End it and ask for a company sign-in.
  if (authService.isPlatformUser()) {
    authService.logout();
    router.navigate(['/login']);
    return false;
  }

  // Admin bypasses all feature and role gates
  const userRoles: string[] = authService.getCurrentUserRole();
  if (userRoles.includes('Admin')) return true;

  // Prefer feature-based authorization if route provides feature keys
  const expectedFeatures = route.data['features'] as string[] | undefined;

  const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user_data') : null;
  const userData: CurrentUserResponse | null = rawUser ? JSON.parse(rawUser) : null;

  // Shared with AuthService — this walk used to be duplicated here verbatim.
  const userFeatureKeys: string[] = extractVisualKeys(userData?.accessFeatures);

  if (expectedFeatures && expectedFeatures.length > 0) {
    const hasFeatureAccess = expectedFeatures.some((key) => userFeatureKeys.includes(key));
    if (hasFeatureAccess) {
      return true;
    }
    router.navigate([homeRouteFor(authService)]);
    return false;
  }

  // Fallback: role-based authorization if no features are specified
  const expectedRoles = route.data['roles'] as string[] | undefined;
  const hasRoleAccess =
    !expectedRoles ||
    expectedRoles.length === 0 ||
    expectedRoles.some((role) => userRoles.includes(role));

  if (hasRoleAccess) {
    return true;
  }

  router.navigate([homeRouteFor(authService)]);
  return false;
};

// platformGuard is gone with the platform area itself. It guarded /admin, which is now the console
// — a separate application on a separate origin, with its own guard that has only one question to
// ask. Nothing in this application should ever again decide whether somebody is an operator.
